import { requestV2 } from '@ltvco/refresh-lib/utils';

import type {
  CreateListBulkReportParams,
  CreateListBulkReportResponse,
  ListBulkReportCurrentResponse,
  ListBulkReportsIndexResponse,
} from './interfaces';

const lizanoApiV2BaseUrl = '/ms/lizano/api/v2';
const bulkReportsV2BaseUrl = `${lizanoApiV2BaseUrl}/bulk_reports`;

const normalizeBulkReportTypeParam = (type: string): string =>
  String(type).trim().replace(/_/g, '-');

const getLizanoHeaders = (token: string) => ({
  'content-type': 'application/json',
  Authorization: `Bearer ${token}`,
});

const getLizanoAuthHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
});

const createListBulkReport = async (
  params: CreateListBulkReportParams,
  token: string
): Promise<CreateListBulkReportResponse> => {
  const bodyPayload: CreateListBulkReportParams = {
    ...params,
    bulk_report: {
      ...params.bulk_report,
      bulk_report_type: normalizeBulkReportTypeParam(
        params.bulk_report.bulk_report_type
      ),
    },
  };

  const init = {
    headers: getLizanoHeaders(token),
    method: 'POST',
    body: JSON.stringify(bodyPayload),
  };

  return requestV2({
    url: bulkReportsV2BaseUrl,
    options: init,
  });
};

const getListBulkReportCurrent = async (
  bulkReportType: string,
  token: string
): Promise<ListBulkReportCurrentResponse> => {
  const params = new URLSearchParams({
    bulk_report_type: normalizeBulkReportTypeParam(bulkReportType),
  });

  const init = {
    headers: getLizanoHeaders(token),
    method: 'GET',
  };

  return requestV2({
    url: `${bulkReportsV2BaseUrl}/current?${params.toString()}`,
    options: init,
  });
};

const isTruthyListBulkReportSuccess = (value: unknown): boolean => {
  if (value === true) {
    return true;
  }
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  return false;
};

export const fetchListBulkReportCurrentValidated = async (
  bulkReportType: string,
  token: string
): Promise<ListBulkReportCurrentResponse> => {
  const raw = await getListBulkReportCurrent(bulkReportType, token);
  const record = raw as unknown as Record<string, unknown>;
  if (!isTruthyListBulkReportSuccess(record.success)) {
    throw new Error('Bulk report status request was not successful');
  }
  return raw;
};

const getListBulkReports = async (
  bulkReportType: string,
  token: string
): Promise<ListBulkReportsIndexResponse> => {
  const params = new URLSearchParams({
    bulk_report_type: normalizeBulkReportTypeParam(bulkReportType),
  });

  const init = {
    headers: getLizanoHeaders(token),
    method: 'GET',
  };

  return requestV2({
    url: `${bulkReportsV2BaseUrl}?${params.toString()}`,
    options: init,
  });
};

const parseErrorBodyMessage = async (response: Response): Promise<string> => {
  const text = await response.text();
  if (!text) {
    return `HTTP ${response.status}`;
  }
  const parsed = JSON.parse(text) as Record<string, unknown>;
  const msg = parsed.message ?? parsed.error;
  if (typeof msg === 'string' && msg.length > 0) {
    return msg;
  }
  return text.length > 200 ? `${text.slice(0, 200)}...` : text;
};

const getBulkReportCsv = async (
  listCode: string,
  bulkReportId: string,
  token: string
): Promise<string> => {
  const query = new URLSearchParams({
    list_id: listCode,
  });
  const url = `${bulkReportsV2BaseUrl}/${encodeURIComponent(
    bulkReportId
  )}/csv?${query.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      ...getLizanoAuthHeaders(token),
      Accept: 'text/csv',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const message = await parseErrorBodyMessage(response);
    throw new Error(message);
  }

  return response.text();
};

export const downloadCsvAsFile = (
  csvContent: string,
  filename: string
): void => {
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8',
  });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = 'noopener';
  anchor.click();
  URL.revokeObjectURL(objectUrl);
};

export const lizanoBulkReportRequests = {
  createListBulkReport,
  getListBulkReportCurrent,
  getListBulkReports,
  getBulkReportCsv,
};
